import { Fragment, useEffect, useMemo, useState } from 'react'

import { Tags, Trash } from 'lucide-react'
import { Badge, Button, ButtonIcon, FormGroup, Input, InputGroup, InputGroupText, Label as FormLabel, Popover, PopoverContent, PopoverTrigger, Select } from 'twyne-new.ui'

import Label from '@Components/pure/rules/Label'
import RuleGroupWrapper from '@Components/pure/rules/RuleGroup'

import { useLogViewerStore } from '../../../store'
import { conditionOptions, conditionsLabels, emptyGroup, emptyTag, parseTagGroups, stringifyTagGroups } from '../../utils'

export default function TagsFilter({ onChange, name, value }) {
  const [open, setOpen] = useState(false)
  const [rules, setRules] = useState(() => parseTagGroups(value))
  const { lists: { tags: { useLoad } } } = useLogViewerStore()

  useLoad()

  useEffect(() => {
    setRules(parseTagGroups(value))
  }, [value])

  const handleOpenChange = open => {
    if (open) {
      setRules(parseTagGroups(value))
    }

    setOpen(open)
  }

  const handleChangeCondition = option => {
    setRules({ ...rules, condition: option ? option.value : null })
  }

  const handleChange = groups => {
    setRules({ ...rules, groups })
  }

  const handleApply = () => {
    onChange(stringifyTagGroups(rules) || null)
    setOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setOpen(false)
  }

  // the condition selects render their dropdown in a sibling portal, so interacting
  // with one must not dismiss this popover
  const handleInteractOutside = event => {
    if (event.target?.closest?.('[data-radix-popper-content-wrapper]')) {
      event.preventDefault()
    }
  }

  return <Popover open={open} onOpenChange={handleOpenChange}>
    <PopoverTrigger className='w-full cursor-pointer'>
      <Trigger value={value} />
    </PopoverTrigger>
    <PopoverContent className='flex max-h-[50vh] w-[32rem] max-w-[calc(100vw-3rem)] flex-col overflow-y-hidden p-0' align='start' onInteractOutside={handleInteractOutside}>
      <div className='min-h-0 flex-1 overflow-y-auto p-4' onKeyDown={event => event.key === 'Enter' && !event.defaultPrevented && event.target.tagName === 'INPUT' && handleApply()}>
        <FormGroup>
          <FormLabel>When match with</FormLabel>
          <InputGroup>
            <Select value={rules.condition} clearable={false} options={conditionOptions} onChange={handleChangeCondition} />
            <InputGroupText addonType='append'>Groups</InputGroupText>
          </InputGroup>
        </FormGroup>
        <Groups name={name} condition={rules.condition} groups={rules.groups} onChange={handleChange} />
        <Button color='success' onClick={() => handleChange([...rules.groups, emptyGroup()])}>Add Group</Button>
      </div>
      <div className='twy-bg-base twy-border-base flex shrink-0 items-center justify-between gap-2 border-t p-4'>
        <span className='min-w-0 flex-1 truncate text-sm twy-text-base' title={stringifyTagGroups(rules)}>
          {stringifyTagGroups(rules) || 'No tags'}
        </span>
        <div className='flex shrink-0 gap-2'>
          <Button size='sm' variant='outline' onClick={handleClear}>Clear</Button>
          <Button size='sm' color='accent' onClick={handleApply}>Apply</Button>
        </div>
      </div>
    </PopoverContent>
  </Popover>
}

function Trigger({ value }) {
  return <InputGroup>
    <Input asChild>
      <div className='block truncate text-left' title={value}>
        {value || <span className='opacity-50'>All tags</span>}
      </div>
    </Input>
    <InputGroupText addonType='append'>
      <Tags size={18} />
    </InputGroupText>
  </InputGroup>
}

function Groups({ name, condition, groups, onChange }) {
  const handleChangeGroup = (groupIndex, group) => {
    onChange(groups.map((current, index) => index === groupIndex ? group : current))
  }

  return groups.map((group, groupIndex, { length }) => <Fragment key={groupIndex}>
    <Group
      name={`${name}-${groupIndex}`}
      group={group}
      onDelete={() => onChange(groups.filter((_, index) => index !== groupIndex))}
      onChange={group => handleChangeGroup(groupIndex, group)}
    />
    {groupIndex < length - 1 && <Condition>{conditionsLabels[condition] || condition}</Condition>}
  </Fragment>)
}

function Group({ name, group, onDelete, onChange }) {
  const handleChangeCondition = option => {
    onChange({ ...group, condition: option ? option.value : null })
  }

  const handleChangeTags = tags => {
    onChange({ ...group, tags: tags.length ? tags : [emptyTag()] })
  }

  const handleChangeTag = (tagIndex, tag) => {
    handleChangeTags(group.tags.map((current, index) => index === tagIndex ? { ...current, ...tag } : current))
  }

  return <RuleGroupWrapper className='twy-border-base-accent'>
    <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
      <div className='flex items-center gap-2'>
        <Label className='whitespace-nowrap'>Match with</Label>
        <Select className='w-[5vw]! min-w-20' value={group.condition} clearable={false} options={conditionOptions} onChange={handleChangeCondition} />
        <Label>conditions</Label>
      </div>
      <div className='flex items-center gap-2'>
        <Button variant='outline' size='sm' onClick={onDelete}>Delete Group</Button>
        <Button variant='outline' size='sm' color='success' onClick={() => handleChangeTags([...group.tags, emptyTag()])}>Add Condition</Button>
      </div>
    </div>
    {group.tags.map((tag, tagIndex, { length }) => <Fragment key={tagIndex}>
      <Tag
        name={`${name}-${tagIndex}`}
        data={tag}
        onChange={tag => handleChangeTag(tagIndex, tag)}
        onDelete={() => handleChangeTags(group.tags.filter((_, index) => index !== tagIndex))}
      />
      {tagIndex < length - 1 && <Condition className='my-2'>{conditionsLabels[group.condition] || group.condition}</Condition>}
    </Fragment>)}
  </RuleGroupWrapper>
}

function Tag({ data: { key, value }, name, onChange, onDelete }) {
  const { lists: { tags: { items } } } = useLogViewerStore()
  const tagOptions = useMemo(() => items.map(tag => ({ label: tag, value: tag })), [items])

  return <div className='flex items-center gap-2'>
    <InputGroup className='min-w-0 flex-1'>
      <Select
        className='min-w-0'
        placeholder='tag'
        value={key}
        options={tagOptions}
        onChange={option => onChange({ key: option.value })}
      />
      <InputGroupText>=</InputGroupText>
      <Input
        id={`${name}-value`}
        className='min-w-0'
        autoComplete='off'
        placeholder='value'
        value={value}
        onChange={event => onChange({ value: event.target.value })}
      />
    </InputGroup>
    <ButtonIcon className='shrink-0' content='Delete' onClick={onDelete}>
      <Trash size={16} />
    </ButtonIcon>
  </div>
}

function Condition({ children, className = '' }) {
  return <Badge variant='light' size='sm' className={`font-semibold uppercase ${className}`}>{children}</Badge>
}
